import AnimationRevealPage from 'helpers/AnimationRevealPage.js';
import tw from 'twin.macro';
import styled from 'styled-components';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { obtenerEmpresa } from '../scripts/obtenerEmpresa';
import { obtenerSucursales } from '../scripts/obtenerSucursales';

const ContactSection = tw.div`py-8 px-4 bg-gray-100 text-gray-900`;
const ContactContainer = tw.div`max-w-screen-xl mx-auto`;
const Title = tw.h2`text-2xl font-bold text-center mb-8`;
const InfoGrid = styled.div`
	${tw`flex flex-wrap justify-center gap-8`}
`;
const InfoCard = styled.div`
	${tw`p-4 bg-white rounded-lg shadow-lg flex flex-col items-center text-center`}
	h3 {
		${tw`text-lg font-semibold mt-4`}
	}
	p {
		${tw`text-sm mt-2 text-gray-600`}
	}
`;
const IconWrapper = tw.div`p-4 bg-primary-500 text-white rounded-full`;

const SucursalesSection = tw.div`py-12 px-4 bg-white text-gray-900`;
const SucursalesContainer = tw.div`max-w-screen-xl mx-auto`;
const SucursalesGrid = styled.div`
	${tw`flex flex-wrap justify-center gap-8`}
`;
const SucursalCard = styled.div`
	${tw`p-6 bg-white rounded-lg shadow-lg border-2 border-gray-200 flex flex-col w-full md:w-80`}
`;
const SucursalName = tw.h3`text-xl font-bold mb-4 text-center`;
const SucursalDetail = tw.p`text-sm text-gray-600 mb-2 flex items-start`;
const DetailIcon = tw.span`mr-2 flex-shrink-0`;
const DetailText = tw.span`flex-1`;
const MapButton = styled.a`
	${tw`mt-auto py-2 px-4 bg-[#99cdb6] text-white rounded-lg text-center font-semibold hover:bg-[#7ab89d] transition-colors duration-200 flex items-center justify-center gap-2 no-underline`}
`;

const ImageSection = tw.div`py-12 px-4 bg-gray-100`;
const ImageContainer = tw.div`max-w-screen-xl mx-auto flex justify-center`;
const LocalImage = tw.img`w-full max-w-2xl mx-auto rounded-lg shadow-lg`;

export default () => {
	const HighlightedText = tw.span`bg-[#99cdb6] text-gray-100 px-4 transform -skew-x-12 inline-block`;
	const dispatch = useDispatch();

	const fetchData = async () => {
		try {
			await obtenerEmpresa(dispatch, sessionStorage.getItem('token'));
			await obtenerSucursales(dispatch);
		} catch (e) {
			console.log(e);
		}
	};

	useEffect(() => {
		fetchData();
	}, []);

	const empresa = useSelector((state) => state.empresa || {});
	const sucursales = useSelector((state) => state.sucursales || []);

	const formatHorario = (horaApertura, horaCierre) => {
		if (!horaApertura || !horaCierre) return null;
		// Remover los segundos (formato HH:mm en lugar de HH:mm:ss)
		const apertura = horaApertura.slice(0, 5);
		const cierre = horaCierre.slice(0, 5);
		return `${apertura} - ${cierre}`;
	};

	return (
		<AnimationRevealPage>
			{/* Datos de Contacto de la Empresa */}
			{empresa && (empresa.correo?.trim() || empresa.telefono?.trim()) && (
				<ContactSection>
					<ContactContainer>
						<Title>
							<HighlightedText> Datos de Contacto </HighlightedText>
						</Title>
						<InfoGrid>
							{empresa.correo?.trim() && (
								<InfoCard>
									<IconWrapper>
										<span role="img" aria-label="email">
											✉️
										</span>
									</IconWrapper>
									<h3>Correo Electrónico</h3>
									<p>{empresa.correo}</p>
								</InfoCard>
							)}
							{empresa.telefono?.trim() && (
								<InfoCard>
									<IconWrapper>
										<span role="img" aria-label="phone">
											📞
										</span>
									</IconWrapper>
									<h3>Teléfono</h3>
									<p>{empresa.telefono}</p>
								</InfoCard>
							)}
						</InfoGrid>
					</ContactContainer>
				</ContactSection>
			)}

			{/* Sección de Sucursales */}
			{sucursales && sucursales.length > 0 && (
				<SucursalesSection>
					<SucursalesContainer>
						<Title>
							<HighlightedText> Nuestras Sucursales </HighlightedText>
						</Title>
						<SucursalesGrid>
							{sucursales.map((sucursal) => (
								<SucursalCard key={sucursal.id}>
									<SucursalName>{sucursal.nombre}</SucursalName>

									<SucursalDetail>
										<DetailIcon>📍</DetailIcon>
										<DetailText>{sucursal.direccion}</DetailText>
									</SucursalDetail>

									{sucursal.telefono && (
										<SucursalDetail>
											<DetailIcon>☎️</DetailIcon>
											<DetailText>{sucursal.telefono}</DetailText>
										</SucursalDetail>
									)}

									{formatHorario(sucursal.hora_apertura, sucursal.hora_cierre) && (
										<SucursalDetail>
											<DetailIcon>🕐</DetailIcon>
											<DetailText>
												<strong>Horarios:</strong>
												<br />
												{formatHorario(sucursal.hora_apertura, sucursal.hora_cierre)}
											</DetailText>
										</SucursalDetail>
									)}

									<MapButton
										href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
											sucursal.direccion,
										)}`}
										target="_blank"
										rel="noopener noreferrer"
									>
										<span>📍</span>
										<span>Ver en el mapa</span>
									</MapButton>
								</SucursalCard>
							))}
						</SucursalesGrid>
					</SucursalesContainer>
				</SucursalesSection>
			)}

			{/* Imagen del Local */}
			<ImageSection>
				<ImageContainer>
					<LocalImage
						src="https://firebasestorage.googleapis.com/v0/b/stellar-fx-444302-f2.firebasestorage.app/o/full%2Fnosotras-local-pocitos.jpeg?alt=media&token=9b7e2ee1-09ec-46c2-851d-67013d7889f5"
						alt="Nuestro local"
					/>
				</ImageContainer>
			</ImageSection>
		</AnimationRevealPage>
	);
};
